import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Trophy, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useLiveStats } from "../context/LiveStats";
import { useT } from "../lib/i18n";

const KEY = "trex_seen_milestones";
const readSeen = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch (e) { return []; }
};
export const milestoneLabel = (v) => `${v / 1000}K`;

export const MilestoneCelebration = () => {
  const { counts } = useLiveStats();
  const { t } = useT();
  const [show, setShow] = useState(null);

  useEffect(() => {
    if (show) return undefined;
    const seen = readSeen();
    const next = (counts.milestones || []).find((m) => !seen.includes(m.id));
    if (next) setShow(next);
    return undefined;
  }, [counts.milestones, show]);

  useEffect(() => {
    if (!show) return undefined;
    localStorage.setItem(KEY, JSON.stringify([...readSeen(), show.id].slice(-50)));
    const id = setTimeout(() => setShow(null), 8000);
    return () => clearTimeout(id);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div key={show.id} data-testid="milestone-celebration" role="status"
          initial={{ opacity: 0, y: -40, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -30 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md overflow-hidden border border-[#1FD1FF]/60 bg-[#050B16]/95 backdrop-blur-2xl shadow-[0_0_40px_rgba(31,209,255,0.35)]">
          <div className="absolute inset-x-0 bottom-0 flex items-end gap-1 h-full opacity-20 pointer-events-none">
            {Array.from({ length: 24 }).map((_, i) => (
              <motion.span key={i} className="flex-1 bg-[#1FD1FF]" initial={{ height: "10%" }}
                animate={{ height: ["15%", `${30 + ((i * 37) % 70)}%`, "20%"] }} transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.04 }} />
            ))}
          </div>
          <div className="relative flex items-center gap-4 p-4">
            <motion.div animate={{ rotate: [0, -12, 12, 0], scale: [1, 1.15, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
              className="w-12 h-12 shrink-0 rounded-full bg-[#1FD1FF] text-black grid place-items-center"><Trophy className="w-6 h-6" /></motion.div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#1FD1FF]">{t("ms.kicker")}</p>
              <p className="font-heavy text-3xl leading-none text-white" data-testid="milestone-value">{milestoneLabel(show.value)} {t("ms.plays")}</p>
              <Link to={`/mix/${show.mix_id}`} onClick={() => setShow(null)} data-testid="milestone-mix-link" className="font-display text-xl truncate block hover:text-[#1FD1FF]">{show.title}</Link>
            </div>
            <button data-testid="milestone-close" onClick={() => setShow(null)} aria-label="Close" className="p-2 text-[#A8B3C4] hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
